import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAccessToken, getAccessToken } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    api.post('/auth/logout').catch(() => {});
  }, []);

  // On mount (page refresh), attempt to restore session via refresh token cookie
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      // If we already have a token in memory (shouldn't happen on fresh load, but guard)
      if (getAccessToken()) {
        try {
          const res = await api.get('/auth/me');
          if (!cancelled) setUser(res.data);
        } catch {
          if (!cancelled) logout();
        }
        if (!cancelled) setLoading(false);
        return;
      }

      // No in-memory token — try to get one via refresh token (httpOnly cookie)
      try {
        const res = await api.post('/auth/refresh');
        const { token, user: userData } = res.data;
        if (!cancelled) {
          setAccessToken(token);
          setUser(userData);
        }
      } catch {
        // No valid refresh token — user is not logged in
        if (!cancelled) {
          setAccessToken(null);
          setUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    }

    restoreSession();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const login = async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { token: newToken, user: userData } = res.data;
    setAccessToken(newToken);
    setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
